import { computed, inject } from 'vue';
import { useStore } from 'vuex';
import { keyBy, merge, values } from 'lodash';

import { GetCurrentConversation, GetBrandStyles, GetRemixImage } from '~/constants/getters';
import { MEDIA_TYPE } from '~/constants/documents';
import { UpdateSystemMessageTranslationData, SetBrandStyles, SetRemixImage } from '~/constants/actions';

import { apiGatewayEndpoints } from '~/services/arcane-api-gateway/api-gateway-endpoints';
import { thumbnailCache } from '~/services/thumbnail-cache-service';

export default () => {
  const store = useStore();
  const arcaneApiGateway = inject('arcaneApiGateway');

  const brandStyles = computed(() => store.getters[`userImagesStore/${GetBrandStyles}`]);
  const currentConversation = computed(() => store.getters[`conversationStore/${GetCurrentConversation}`]);

  const remixImage = computed(() => store.getters[`userImagesStore/${GetRemixImage}`]);
  const setUserImageForRemix = (image) => {
    store.dispatch(`userImagesStore/${SetRemixImage}`, image);
  };
  const updateGeneratedImagesData = async (messageId, data, userConversationId = '') => {
    const imageIds = data.image_ids || [];
    const response = await getUserImages({
      ids: imageIds,
      forceRefresh: false,
      userConversationId,
    });
    const newData = { ...data, images: response };
    store.dispatch(`conversationStore/${UpdateSystemMessageTranslationData}`, { messageId, data: [newData] });
  };
  const getBrandStylePreviews = async ({ previewImageKeys = [] }) => {
    const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.brandStylePreviews, {
      previewImageKeys,
    });
    return data;
  };

  const retrieveImages = async ({ from = 0, size = 0 } = {}) => {
    try {
      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.userImages.userImages, {
        from,
        size,
      });

      const imageIds = (data.images || []).map((image) => image.id);
      const thumbs = await getUserImages({ ids: imageIds, forceRefresh: false });
      const updatedImages = values(merge(keyBy(data.images, 'id'), keyBy(thumbs, 'id')));

      return { images: updatedImages, total: data.total };
    } catch (error) {
      console.log('Failed to retrieve user images', error);
      return { images: [], total: 0 };
    }
  };

  const getUserImages = async ({ ids = [], forceRefresh = false, userConversationId = '' }) => {
    try {
      // Simple caching for user images
      const uncachedIds = [];
      const cachedResults = [];

      for (const id of ids) {
        if (forceRefresh) {
          // Skip cache when force refresh is requested
          uncachedIds.push(id);
          continue;
        }

        const cachedUrl = thumbnailCache.get(id, MEDIA_TYPE.userImages, 'original');
        if (cachedUrl) {
          const cachedImages = thumbnailCache.getAllVariants(id, MEDIA_TYPE.userImages);
          if (cachedImages && cachedImages.length > 0) {
            cachedResults.push(...cachedImages);
          } else {
            cachedResults.push({
              id,
              url: cachedUrl,
              name: 'original.png',
              variant: 'original',
            });
          }
        } else {
          uncachedIds.push(id);
        }
      }

      if (uncachedIds.length === 0) {
        return cachedResults;
      }

      const { data } = await arcaneApiGateway.post(apiGatewayEndpoints.s3Docs.userGeneratedImages, {
        ids: uncachedIds,
        userConversationId,
      });

      // Cache all variants returned from BE
      // Backend returns variant field: 'original' or 'cropped'
      for (const image of data) {
        if (image.url && image.variant) {
          // For cropped images, use the name as cache key to support multiple crops
          const cacheVariant = image.variant === 'cropped' && image.name ? image.name : image.variant;
          thumbnailCache.set(image.id, MEDIA_TYPE.userImages, image.url, cacheVariant);
        }
      }

      return [...cachedResults, ...data];
    } catch (error) {
      console.error('Failed to load user image thumbs:', error);
      return [];
    }
  };

  const getBrandStyleById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.userImages.getBrandStyleById(id));
      return data;
    } catch (error) {
      console.log('Failed to retrieve brand style', error);
    }
  };
  const getBrandStyles = async () => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.userImages.brandStyles);

      // Get preview URLs for all brand styles
      if (data && data.length > 0) {
        const previewImageKeys = data.map((brandStyle) => brandStyle.previewImageKey);
        const previews = await getBrandStylePreviews({ previewImageKeys });

        // Merge preview URLs into brand styles
        const brandStylesWithPreviews = values(merge(keyBy(data, 'previewImageKey'), keyBy(previews, 'previewImageKey')));

        // Store in userImagesStore
        store.dispatch(`userImagesStore/${SetBrandStyles}`, brandStylesWithPreviews);

        return brandStylesWithPreviews;
      }

      store.dispatch(`userImagesStore/${SetBrandStyles}`, []);
      return [];
    } catch (error) {
      console.log('Failed to retrieve brand styles', error);
      store.dispatch(`userImagesStore/${SetBrandStyles}`, []);
      return [];
    }
  };

  const deleteUserImageById = async (id) => {
    try {
      await arcaneApiGateway.delete(apiGatewayEndpoints.userImages.deleteUserImageById(id));
    } catch (error) {
      console.log('Failed to delete user image', error);
    }
  };

  const getUserImageById = async (id) => {
    try {
      const { data } = await arcaneApiGateway.get(apiGatewayEndpoints.userImages.getUserImageById(id));
      return data;
    } catch (error) {
      console.log('Failed to get user image', error);
    }
  };

  const downloadUserImage = async (id, title, userConversationId = '') => {
    const thumbnailUrl = await getUserImages({ ids: [id], forceRefresh: true, userConversationId });
    if (thumbnailUrl.length > 0) {
      const url = thumbnailUrl[0].url;

      // Open in new tab
      window.open(url, '_blank');

      // Download the image
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = title || 'image';
        link.click();

        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading image:', error);
      }
    } else {
      console.error('No thumbnail URL found for image', id);
    }
  };

  return {
    currentConversation,
    retrieveImages,
    getUserImages,
    getBrandStyleById,
    getBrandStyles,
    deleteUserImageById,
    updateGeneratedImagesData,
    downloadUserImage,
    getUserImageById,
    brandStyles,
    setUserImageForRemix,
    remixImage,
  };
};
